/**
 * **Parsing usage** (`/settings/usage`, engine W16, T16.9, FR-E35, FR-E36,
 * D43; C-E11; W28, FR-65, plan D86) — read only, no bar: the parses that ended
 * in a month and how, the calls to an AI model, and what they cost in rupees
 * from integer paise, by the document type read. Counts and money only; the
 * service sends no document. A job counts in the month it ended, in IST.
 *
 * The organisation's settings administrator's alone (`settings:can_update`):
 * a reader is told so and **nothing is asked of the service**. A row of figures
 * and a table, not a chart — a handful of counts, one month at a time. The
 * current month is the service's, from its first answer; the eleven before it
 * are a step back each — buttons, not a picker: a month is read, not a setting.
 * The service leaves the plumbing's own profiles out; the table drops them too.
 *
 * **Engine W32 (FR-E53):** a structured or mapped reading that called no model uses
 * no unit, so a service from W32 on also says how many completed that way —
 * *Read without a model*, beside the parses, and per document type. An answer
 * without it is shown as before.
 */

import { SETTINGS, formatPaise, useLoad } from '@fiveox/prodoc-ui'
import { Button, DataTable, EmptyState, Skeleton, SkeletonRegion, SkeletonText, cn, formatMonthYear, formatNumber, type ColumnDef } from '@fiveox/ui'
import { ChartColumn, ChevronLeft, ChevronRight } from 'lucide-react'
import { useEffect, useState } from 'react'

import { EditPage } from '../../components/edit'
import { SettingsSection } from '../../components/settings-parts'
import { isIdleMonth, isInternalProfile, profileLabelOf, recentMonths, type ProfileUsage } from '../../lib/parsing'
import { useSettingsHost } from './host'
import { SETTINGS_BACK } from './settings-data'

/** `2026-10` → "October 2026". */
export const monthText = (month: string): string => formatMonthYear(`${month}-01`)

export function UsageSettings() {
  const { can, orgName } = useSettingsHost()
  return (
    <EditPage
      back={SETTINGS_BACK}
      title="Parsing usage"
      description={`What ProDoc read for ${orgName} in a month — asked by a person or by an app — and what the AI it needed cost.`}
    >
      {/* C-E11, FR-E36: the administrator's — a reader is told so, and the service is asked nothing. */}
      {can(SETTINGS, 'can_update') ? (
        <UsageMonth />
      ) : (
        <EmptyState title="Parsing usage is the administrator’s" description="Only a ProDoc administrator sees what the organisation’s parsing cost. Ask one if you need the figures." />
      )}
    </EditPage>
  )
}

function UsageMonth() {
  const { client, parsing } = useSettingsHost()
  // The types' labels, as everywhere a profile is named.
  const settings = useLoad(() => client.settings.get(), [client])
  const options = useLoad(() => client.documents.options(), [client])
  const labelOf = profileLabelOf(options.data, settings.data)
  // `null` asks for the current month, which the service names in its answer.
  const [month, setMonth] = useState<string | null>(null)
  const [latest, setLatest] = useState<string | null>(null)
  const usage = useLoad(async () => ({ asked: month, usage: await parsing.usage(month) }), [parsing, month])
  const answered = usage.data
  useEffect(() => {
    if (answered && answered.asked === null && answered.usage.month) setLatest(answered.usage.month)
  }, [answered])
  // The month asked for, never the last one shown while another loads.
  const shown = answered && answered.asked === month ? answered.usage : null
  const named = shown?.month ?? month ?? latest
  const monthName = named ? monthText(named) : 'this month'
  const rows = shown ? shown.by_profile.filter((p) => !isInternalProfile({ name: p.profile })) : []
  // One month at a time, back as far as the eleven before the current one.
  const months = latest ? recentMonths(latest) : []
  const at = months.indexOf(month ?? latest ?? '')
  const older = at >= 0 ? (months[at + 1] ?? null) : null
  const newer = at > 0 ? (months[at - 1] ?? null) : null
  // W32 (FR-E53): said only by a service that counts it; absent, the screen is as it was.
  const withoutModel = shown?.parses.read_without_model
  const counted = withoutModel !== undefined
  const perProfile = rows.some((p) => p.read_without_model !== undefined)

  const columns: ColumnDef<ProfileUsage>[] = [
    {
      key: 'profile',
      label: 'Document type',
      width: 220,
      required: true,
      render: (p) => (
        <span>
          <span className="text-foreground font-medium">{labelOf(p.profile)}</span> <span className="text-muted-foreground font-mono text-xs">{p.profile}</span>
        </span>
      ),
    },
    { key: 'completed', label: 'Completed', width: 110, align: 'right', render: (p) => <span className="tabular-nums">{formatNumber(p.completed)}</span> },
    ...(perProfile
      ? [
          {
            key: 'read_without_model',
            label: 'Without a model',
            width: 140,
            align: 'right' as const,
            render: (p: ProfileUsage) => <span className="tabular-nums">{formatNumber(p.read_without_model ?? 0)}</span>,
          },
        ]
      : []),
    { key: 'refused', label: 'Refused', width: 110, align: 'right', render: (p) => <span className="tabular-nums">{formatNumber(p.refused)}</span> },
    { key: 'model_calls', label: 'Model calls', width: 120, align: 'right', render: (p) => <span className="tabular-nums">{formatNumber(p.model_calls)}</span> },
    { key: 'ai_cost', label: 'AI cost', width: 130, align: 'right', render: (p) => <span className="tabular-nums">{formatPaise(p.ai_cost_paise)}</span> },
  ]

  return (
    <SettingsSection
      id="usage"
      icon={<ChartColumn size={18} aria-hidden />}
      title="The month’s parsing"
      description="A request counts in the month it ended, India time."
      action={
        latest ? (
          <span className="flex items-center gap-1 text-sm">
            <Button variant="ghost" size="sm" disabled={!older} onClick={() => older && setMonth(older)} aria-label="Previous month">
              <ChevronLeft size={16} aria-hidden />
            </Button>
            <span className="text-foreground min-w-28 text-center font-medium" aria-live="polite" data-usage-month={month ?? latest}>
              {monthText(month ?? latest)}
            </span>
            <Button variant="ghost" size="sm" disabled={!newer} onClick={() => newer && setMonth(newer)} aria-label="Next month">
              <ChevronRight size={16} aria-hidden />
            </Button>
          </span>
        ) : undefined
      }
    >
      <div className="space-y-4">
        {shown ? (
          isIdleMonth(shown) ? (
            <EmptyState variant="outline" title={`Nothing read in ${monthName}`} description="A document is read only when a person or an app asks for it. Each request is counted here in the month it ends." />
          ) : (
            <>
              <dl className={cn('grid gap-3 sm:grid-cols-2', counted ? 'lg:grid-cols-5' : 'lg:grid-cols-4')} aria-label={`Parsing in ${monthName}`}>
                <UsageFigure
                  label="Parses completed"
                  value={formatNumber(shown.parses.completed)}
                  note={counted ? 'Each counts as one document processed on the plan, unless it was read without a model.' : 'Each counts as one document processed on the plan.'}
                />
                {counted && (
                  <UsageFigure
                    label="Read without a model"
                    value={formatNumber(withoutModel)}
                    note="Read from a published format or under a column map. No model ran, so none is counted as a document processed."
                  />
                )}
                <UsageFigure
                  label="Refused"
                  value={formatNumber(shown.parses.refused)}
                  note={
                    shown.parses.failed > 0
                      ? `Not read, and free. ${formatNumber(shown.parses.failed)} more could not be finished, and are free too.`
                      : 'Not read — the wrong type, unreadable or empty — and free.'
                  }
                />
                <UsageFigure
                  label="Model calls"
                  value={formatNumber(shown.model_calls)}
                  note={shown.model_calls > 0 ? 'Calls to an AI model, for the parts a document needed help to read.' : 'No part of a document went to an AI model.'}
                />
                <UsageFigure label="AI cost" value={formatPaise(shown.ai_cost_paise)} note="What those calls cost." />
              </dl>
              {rows.length > 0 && <DataTable columns={columns} rows={rows} rowKey={(p) => p.profile} />}
            </>
          )
        ) : usage.error && !usage.loading ? (
          <p className="text-muted-foreground text-sm">
            The usage for {monthName} could not be read: {usage.error}{' '}
            <Button variant="link" onClick={() => void usage.reload()}>
              Try again
            </Button>
          </p>
        ) : (
          <SkeletonRegion label="Loading the month’s parsing usage" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="border-border space-y-2 rounded-xl border p-4">
                <SkeletonText w="w-24" />
                <Skeleton className="h-7 w-16" />
              </div>
            ))}
          </SkeletonRegion>
        )}
      </div>
    </SettingsSection>
  )
}

/** One headline figure of the month: its name, the figure, and what it means. */
function UsageFigure({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div className="border-border rounded-xl border p-4">
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className="text-foreground mt-1 text-2xl font-semibold tabular-nums">{value}</dd>
      <dd className="text-muted-foreground mt-1 text-xs">{note}</dd>
    </div>
  )
}
